import { AvailablePlan } from '../../types/AvailablePlan'
import { Plan } from '@dtx-company/true-common/src/types/Plan'
import { SubscriptionServiceAvailablePlan } from './types/models/SubscriptionServiceAvailablePlan'
import { SubscriptionServiceGetAvailablePlansJSON } from './types/jsons/SubscriptionServiceGetAvailablePlans.JSON'
import { fetchSubscriptionServiceUrl } from './fetchSubscriptionServiceUrl'
import { getPlans } from './getPlans'

const GET_AVAILABLE_PLANS_URL = `/v2/available-plans`

interface GetAvailablePlansOptionsFilter {
  planPricingGroup?: string
}
export interface GetAvailablePlansOptions {
  filter?: GetAvailablePlansOptionsFilter
  include?: {
    plan?: boolean
  }
  token?: string
}

function buildAvailablePlansFromJSON(
  jsonAvailablePlans: SubscriptionServiceAvailablePlan[],
  plans: Plan[]
): AvailablePlan[] {
  return jsonAvailablePlans.map(jsonAvailablePlan => {
    return {
      availablePlanId: jsonAvailablePlan.availablePlanId,
      planId: jsonAvailablePlan.planId,
      planChangeType: jsonAvailablePlan.planChangeType,
      subscriptionId: jsonAvailablePlan.subscriptionId ?? undefined,
      plan: plans.find(plan => plan.id === jsonAvailablePlan.planId)
    }
  })
}

/**
 * Calls the subscription service: GET /v2/available-plans endpoint.
 * @returns AvailablePlan[] or [] if no plans are available.
 * @docs https://backstage.use1.dev.services.flowcode.systems/catalog/default/api/subscription-service-rest-api/definition#/Available%20Plans/get_v2_available_plans
 */
export async function getAvailablePlans(
  options: GetAvailablePlansOptions
): Promise<AvailablePlan[]> {
  const response = await fetchSubscriptionServiceUrl<SubscriptionServiceGetAvailablePlansJSON>({
    query: {
      'filter[planPricingGroup]': options.filter?.planPricingGroup
    },
    token: options.token,
    url: GET_AVAILABLE_PLANS_URL
  })

  if ([401, 403, 404].includes(response.status)) {
    return []
  }

  if (!response.ok) {
    throw new Error(
      `Received http status ${response.status} from ${GET_AVAILABLE_PLANS_URL} endpoint`
    )
  }

  if (!response.json) {
    throw new Error(`The ${GET_AVAILABLE_PLANS_URL} endpoint did not return a json payload`)
  }

  const jsonAvailablePlans = response.json?.availablePlans || []

  const plans = options.include?.plan
    ? await getPlans({
        filter: { planId: jsonAvailablePlans.map(jsonAvailablePlan => jsonAvailablePlan.planId) }
      })
    : []

  const availablePlans = buildAvailablePlansFromJSON(jsonAvailablePlans, plans)
  return availablePlans
}
